<script setup lang="ts">
import { ref, watch } from "vue";

import type { Headers, PaginationMeta } from "@/types/rest";
import type {
  ConfiguredEntity,
  IntegrationInstance,
  EntityFilterData
} from "@/types/integrationInstance";
import type { IncludedEntity } from "@/types/activity";

import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { getPaginationLimit, savePaginationLimit } from "@/mixins/listing";

import { integrationsStore } from "@/stores/integrations";

import EntityListFiltered from "@/components/elements/entity/EntityListFiltered.vue";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  pageId: {
    type: String,
    default: '',
  },
  filteredEntities: {
    type: Array,
    required: true,
  },
  selectedEntities: {
    type: Array,
    required: false,
    default: [],
  },
  instances: {
    type: Array,
    required: false,
    default: [],
  },
});

const integrationStorage = integrationsStore();
const { sleep } = useTiming();
useComponentErrorCapture();

const emit = defineEmits(["close", "addToPage"]);

const entityListFilterDefaults = {
  searchText: '',
  entityTypes: '',
  instances: ''
}

const filteredEntities = ref<ConfiguredEntity[]>([]);
const selectedEntities = ref<IncludedEntity[]>([]);
const instances = ref<IntegrationInstance[]>([]);

const entityListFilter = ref<EntityFilterData>(entityListFilterDefaults);

const showList = ref(false); // For transition, animation

const pagination = ref<PaginationMeta>({
  limit: getPaginationLimit() ?? 20,
  page: 1,
});

defineExpose({
  loadData
});

watch(props, async (val) => {
  selectedEntities.value = val.selectedEntities as IncludedEntity[];
  instances.value = val.instances as IntegrationInstance[];

  if (val.show == true && showList.value == false) {
    entityListFilter.value = entityListFilterDefaults;
    showList.value = true;
    loadData();
  } else if (showList.value == true && val.show == false) {
    await sleep(1000);
    showList.value = false;
  }
});

watch(
  () => pagination.value.limit,
  (val) => {
    savePaginationLimit(val);
  }
);

integrationStorage.$subscribe((mutation, state) => {
  updateFilteredEntities(state.configuredEntities);
});

function changeFilter(data: EntityFilterData) {
  entityListFilter.value = data;
  fetchFilteredEntities(true);
}

function reloadEntities() {
  fetchFilteredEntities(true);
}

async function addToPage(entities: ConfiguredEntity[]) {
  emit('addToPage', entities);
}

async function fetchFilteredEntities(userFetchFirstPage: boolean = false) {
  if (userFetchFirstPage === true) {
    pagination.value.page = 1;
  }

  let searchText = entityListFilter.value.searchText;

  try {
    const entList = await integrationStorage.getConfiguredEntitiesByPageByLimit(entityListFilter.value.instances || '', false, pagination.value.page, pagination.value.limit, searchText, entityListFilter.value.entityTypes, props.pageId);
    filteredEntities.value = entList.data.configuredEntities as ConfiguredEntity[];

    const listHeaders = entList.headers as Headers;
    if (listHeaders) {
      const headerLimit = Number(listHeaders['pagination-limit']);
      const newLimit = headerLimit <= Number(listHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
      pagination.value = {
        count: Number(listHeaders['pagination-count']) || 0,
        limit: newLimit || 0,
        page: Number(listHeaders['pagination-page']) || 0,
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function updateFilteredEntities(configuredList: ConfiguredEntity[]) {
  for (let index = 0; index < filteredEntities.value.length; index++) {
    for (let ind = 0; ind < configuredList.length; ind++) {
      if (filteredEntities.value[index].entity_id == configuredList[ind].entity_id && 
      JSON.stringify(filteredEntities.value[index]) != JSON.stringify(configuredList[ind])) {
        filteredEntities.value[index] = configuredList[ind];
      }
    }
  }
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchFilteredEntities();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchFilteredEntities();
}

function close() {
  emit("close");
}

async function loadData() {
  selectedEntities.value = props.selectedEntities as IncludedEntity[];
  instances.value = props.instances as IntegrationInstance[];
  await fetchFilteredEntities(true);
}

</script>
<template>
  <div class="custom-remote-modify custom-remote-add-entities">
    <div class="custom-remote-modify__header custom-remote-add-entities__header">
      <button
        @click="close"
        class="button button--blank button--icon button--icon--medium">
        <i class="fa-regular fa-arrow-left"></i>
      </button>
      <span>{{ $t("customise_remote.add_entities.title") }}</span>
    </div>
    <EntityListFiltered
      v-if="show || showList"
      @change-filter="changeFilter"
      @reload-entities="reloadEntities"
      @change-page="changePage"
      @change-per-page="changePerPage"
      @add-entities="addToPage"
      :pagination="pagination"
      :all-entities="filteredEntities"
      :instances="instances"
      :drag-group="'customise-remote-items'"
      :has-quick-options="true"
    />
  </div>
</template>